<template>
  <v-card flat>
    <v-card-text>
      <div v-if="loading" class="text-center py-6">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else>
        <v-expansion-panels v-model="openGroups" multiple flat>
          <v-expansion-panel
            v-for="group in documentGroups"
            :key="group.key"
          >
            <v-expansion-panel-header>
              <div class="font-weight-medium">
                {{ group.label }}
                <v-chip x-small class="ml-2">{{ group.count }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="!group.documents.length" class="grey--text">
                {{ $t("DEALS.VIEW.NO_DOCUMENTS") }}
              </div>
              <v-card
                v-for="doc in group.documents"
                :key="`${group.key}-${doc.id}`"
                outlined
                class="mb-4"
              >
                <v-card-subtitle class="pb-0">
                  <router-link :to="documentRoute(doc)">
                    {{ doc.code }}
                  </router-link>
                  <v-chip x-small class="ml-2">{{ doc.status_label }}</v-chip>
                  <v-chip v-if="doc.type_label" x-small outlined class="ml-2">
                    {{ doc.type_label }}
                  </v-chip>
                  <v-chip v-if="doc.direction_label" x-small outlined class="ml-2">
                    {{ doc.direction_label }}
                  </v-chip>
                </v-card-subtitle>
                <v-card-text>
                  <DealWorkflowFiles
                    :files="doc.files"
                    :downloading-file-id="downloadingFileId"
                    :show-toggle="false"
                    @download="downloadDocumentFile(doc, $event)"
                  />
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import DealWorkflowFiles from "@/views/pages/deals/partials/DealWorkflowFiles.vue";
import { nodeRoute } from "@/views/pages/deals/dealWorkflowMeta";
import { GET_DEAL_STOCK_OVERVIEW } from "@/store/deals.module";
import { DOWNLOAD_STOCK_DELIVERY_NOTE_FILE } from "@/store/stock/stockDeliveryNotes.module";
import { DOWNLOAD_STOCK_PO_FILE } from "@/store/stock/stockPurchaseOrders.module";
import { DOWNLOAD_STOCK_RECEIPT_FILE } from "@/store/stock/stockReceipts.module";
import { DOWNLOAD_STOCK_SO_FILE } from "@/store/stock/stockSalesOrders.module";
import { DOWNLOAD_STOCK_SALE_FILE } from "@/store/stock/stockSales.module";
import { DOWNLOAD_STOCK_SHIPMENT_FILE } from "@/store/stock/stockShipments.module";
import { DOWNLOAD_STOCK_TO_FILE } from "@/store/stock/stockTransportOrders.module";
import { DOWNLOAD_STOCK_INVOICE_FILE } from "@/store/stock/stockInvoices.module";
import { SHOW_SNACK } from "@/store/snack.module";

const DOWNLOAD_BY_KIND = {
  purchase_order: DOWNLOAD_STOCK_PO_FILE,
  sales_order: DOWNLOAD_STOCK_SO_FILE,
  receipt: DOWNLOAD_STOCK_RECEIPT_FILE,
  delivery: DOWNLOAD_STOCK_SALE_FILE,
  delivery_note: DOWNLOAD_STOCK_DELIVERY_NOTE_FILE,
  transport_order: DOWNLOAD_STOCK_TO_FILE,
  shipment: DOWNLOAD_STOCK_SHIPMENT_FILE,
  invoice: DOWNLOAD_STOCK_INVOICE_FILE,
};

export default {
  name: "DealDocumentsTab",
  components: { DealWorkflowFiles },
  props: {
    dealId: {
      type: Number,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      documentGroups: [],
      openGroups: [0, 1, 2, 3, 4, 5, 6],
      downloadingFileId: null,
    };
  },
  watch: {
    dealId() {
      if (this.active) {
        this.loadDocuments();
      }
    },
    active(value) {
      if (value) {
        this.loadDocuments();
      }
    },
  },
  created() {
    if (this.active) {
      this.loadDocuments();
    }
  },
  methods: {
    documentRoute(doc) {
      return nodeRoute({ kind: doc.document_kind, id: doc.id });
    },
    async loadDocuments() {
      if (!this.dealId) {
        return;
      }
      this.loading = true;
      try {
        const data = await this.$store.dispatch(GET_DEAL_STOCK_OVERVIEW, this.dealId);
        this.documentGroups = data.document_groups || [];
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    downloadDocumentFile(doc, file) {
      const ownerKind = file?.shipment_id ? "shipment" : doc.document_kind;
      const ownerId = file?.shipment_id || doc?.id;
      const action = DOWNLOAD_BY_KIND[ownerKind];
      if (!action || !ownerId || !file?.id) {
        return;
      }

      this.downloadingFileId = file.id;
      this.$store
        .dispatch(action, {
          id: ownerId,
          fileId: file.id,
          originalName: file.original_name,
          name: file.name,
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message:
              (error && error.message) ||
              this.$t("GENERAL.UNEXPECTED_ERROR"),
            color: "error",
          });
        })
        .finally(() => {
          this.downloadingFileId = null;
        });
    },
  },
};
</script>
